// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Recovering the PRINTED page number — the one an author cites as "p. 14" — by
 * reading the folio off the page.
 *
 * THIS IS THE PRIMARY SOURCE, AND getPageLabels() IS ONLY CORROBORATION. That
 * inversion is the opposite of the obvious architecture and it was forced by
 * measurement, over 22 PDFs / 2,207 pages / 20+ producers (pdfTeX, xdvipdfmx,
 * Distiller 4.05 through 11.0, Adobe PDF Library 9.9 through 17.0, InDesign
 * CS5.5 through 18.1, Arbortext, 3B2, QuarkXPress, Acrobat PDFMaker for Word,
 * iText):
 *
 *   - Harvesting the folio: 2,054/2,207 pages labelled = 93.1% coverage, and
 *     177/177 correct against ground truth taken from OUTSIDE the file (PMC OA
 *     citation strings, the Federal Register API), plus 17/17 correct against
 *     folios read off the page by eye. Zero wrong labels.
 *   - "Trust /PageLabels, harvest as a fallback" is WRONG on 75 of 181
 *     ground-truth pages, and invents 16 labels out of 16 on a paper that has no
 *     printed page numbers at all.
 *   - The UN Sustainable Development Goals Report 2023 carries non-identity
 *     /PageLabels that are silently off by +2 across 75 consecutive pages,
 *     because two unnumbered cover sheets were folded into the series.
 *   - Five of the 22 documents carry IDENTITY labels (1, 2, 3 …) that are a flat
 *     lie: the true printed first pages of those journal articles are 287, 818,
 *     87 and 407. Identity labels are therefore treated as ABSENT, not as data.
 *
 * TWO PARAMETERS ARE LOAD-BEARING AND MUST NOT BE "TIDIED".
 *
 * BAND = 0.22, not the 0.08 that seems obviously right. Measured coverage:
 * 0.08 -> 74.9%, 0.10 -> 75.3%, 0.12 -> 75.4%, 0.15 -> 92.4%, 0.22 -> 93.1%.
 * The cliff between 0.12 and 0.15 is a 417-page textbook whose folio lives in a
 * running head at 13.5% of page height; the last step recovers a Senate document
 * whose folio sits at 78.8% of a short trim size.
 *
 * THE GEOMETRIC GUARD. A real folio sits at the same height, in at most two
 * columns, on every page of its run. A bibliography year does not. Without the
 * guard, a paper with NO page numbers had 9 of its 16 pages labelled from the
 * years in its reference list — confident, consistent, and wrong. With it, that
 * document correctly yields nothing. Any future change that loosens these
 * thresholds to raise coverage must be re-run against a no-folio control.
 */

export const FOLIO_BAND = 0.22;

const ROMAN_RE = /^(m{0,3})(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})$/;

export function romanToInt(s: string): number | null {
  const t = s.toLowerCase();
  if (!t.length || !ROMAN_RE.test(t)) return null;
  const map: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
  let total = 0;
  for (let i = 0; i < t.length; i++) {
    const v = map[t[i]];
    const next = map[t[i + 1]] ?? 0;
    total += v < next ? -v : v;
  }
  return total;
}

export function intToRoman(n: number): string {
  const table: [number, string][] = [
    [1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'], [100, 'c'], [90, 'xc'],
    [50, 'l'], [40, 'xl'], [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i'],
  ];
  let out = '';
  let rest = n;
  for (const [value, numeral] of table) {
    while (rest >= value) {
      out += numeral;
      rest -= value;
    }
  }
  return out;
}

export interface BandItem {
  str: string;
  /** Device-space x, top-left origin. */
  x: number;
  /** Device-space y, top-left origin. */
  y: number;
}

export interface FolioCandidate {
  value: number;
  kind: 'arabic' | 'roman';
  /** The token is essentially the whole text item — folios are typeset alone. */
  bare: boolean;
  xFrac: number;
  yFrac: number;
}

export function candidatesForPage(items: BandItem[], height: number, width: number, band = FOLIO_BAND): FolioCandidate[] {
  const out: FolioCandidate[] = [];
  const topLimit = height * band;
  const botLimit = height * (1 - band);
  for (const item of items) {
    const s = item.str.trim();
    if (!s) continue;
    if (item.y > topLimit && item.y < botLimit) continue;

    const tokens = s.split(/[\s|/\\[\](){}<>,;:•·–—‒−-]+/).filter(Boolean);
    if (tokens.length !== 1 || tokens[0] !== s) tokens.push(s);

    const seen = new Set<string>();
    for (const rawToken of tokens) {
      const token = rawToken.replace(/^[.'"’“”]+|[.'"’“”]+$/g, '');
      if (!token || seen.has(token)) continue;
      seen.add(token);

      const bare = token === s.replace(/^[\s.[\](){}'"’“”|–—-]+|[\s.[\](){}'"’“”|–—-]+$/g, '');
      let kind: 'arabic' | 'roman' | null = null;
      let value = 0;

      if (/^\d{1,5}$/.test(token)) {
        kind = 'arabic';
        value = parseInt(token, 10);
      } else if (/^[ivxlcdmIVXLCDM]{1,7}$/.test(token) && (token === token.toUpperCase() || token === token.toLowerCase())) {
        const v = romanToInt(token);
        // Capped at 200 because roman folios are front matter, and because
        // without the cap ordinary words that happen to be valid numerals
        // qualify — "mix" is 1009, "mid" is 1499, "dim" is 1499.
        if (v !== null && v >= 1 && v <= 200) {
          kind = 'roman';
          value = v;
        }
      }
      if (kind === null || value < 1) continue;
      out.push({ value, kind, bare, xFrac: item.x / width, yFrac: item.y / height });
    }
  }
  return out;
}

/** Indices of the largest group of values lying within `tol` of each other. */
function largestCluster(values: number[], tol: number): number[] {
  const order = values.map((_, i) => i).sort((a, b) => values[a] - values[b]);
  let best: number[] = [];
  for (let i = 0; i < order.length; i++) {
    const run = [order[i]];
    for (let j = i + 1; j < order.length; j++) {
      if (values[order[j]] - values[order[i]] <= tol) run.push(order[j]);
      else break;
    }
    if (run.length > best.length) best = run;
  }
  return best;
}

export interface FolioRun {
  kind: 'arabic' | 'roman';
  offset: number;
  firstSheet: number;
  lastSheet: number;
  hits: number;
}

export interface HarvestResult {
  /** sheet (1-based) -> printed page value. */
  assign: Map<number, { value: number; kind: 'arabic' | 'roman' }>;
  runs: FolioRun[];
}

export interface HarvestOptions {
  minRunPages?: number;
  minRunDensity?: number;
  yTol?: number;
  xTol?: number;
  minGeomShare?: number;
  geometricGuard?: boolean;
}

export function harvest(pages: FolioCandidate[][], options: HarvestOptions = {}): HarvestResult {
  const minRunPages = options.minRunPages ?? 3;
  const minRunDensity = options.minRunDensity ?? 0.6;
  const yTol = options.yTol ?? 0.012; // roughly 10pt on a 792pt page
  const xTol = options.xTol ?? 0.1; // recto/verso folio column drift
  const minGeomShare = options.minGeomShare ?? 0.7;
  const guard = options.geometricGuard ?? true;

  const total = pages.length;
  const assign: HarvestResult['assign'] = new Map();
  const runs: FolioRun[] = [];
  const taken = new Set<number>();

  for (const kind of ['arabic', 'roman'] as const) {
    // Vote on the offset k = printedValue - sheetIndex. A real folio series has
    // one k across a long stretch of pages.
    const votes = new Map<number, Map<number, { c: FolioCandidate; score: number }>>();
    for (let sheet = 1; sheet <= total; sheet++) {
      for (const c of pages[sheet - 1]) {
        if (c.kind !== kind) continue;
        const k = c.value - sheet;
        let perSheet = votes.get(k);
        if (!perSheet) {
          perSheet = new Map();
          votes.set(k, perSheet);
        }
        const score = (c.bare ? 2 : 0) + (c.yFrac > 0.5 ? 1 : 0);
        const prev = perSheet.get(sheet);
        if (!prev || score > prev.score) perSheet.set(sheet, { c, score });
      }
    }

    const ordered = [...votes.entries()].sort((a, b) => b[1].size - a[1].size);
    for (const [k, perSheet] of ordered) {
      let entries = [...perSheet.entries()].filter(([sheet]) => !taken.has(sheet));
      if (entries.length < minRunPages) continue;

      if (guard) {
        const ys = entries.map(([, v]) => v.c.yFrac);
        const keepY = largestCluster(ys, yTol);
        if (keepY.length / entries.length < minGeomShare) continue;
        entries = keepY.map((i) => entries[i]);

        const xs = entries.map(([, v]) => v.c.xFrac);
        const first = largestCluster(xs, xTol);
        const firstSet = new Set(first);
        const restIdx = xs.map((_, i) => i).filter((i) => !firstSet.has(i));
        const second = largestCluster(
          restIdx.map((i) => xs[i]),
          xTol,
        );
        if ((first.length + second.length) / xs.length < minGeomShare) continue;
        const keep = new Set([...first, ...second.map((j) => restIdx[j])]);
        entries = entries.filter((_, i) => keep.has(i));
      }

      const bySheet = new Map(entries);
      const sheets = [...bySheet.keys()].sort((a, b) => a - b);
      if (sheets.length < minRunPages) continue;

      let i = 0;
      while (i < sheets.length) {
        let j = i;
        let best: { i: number; j: number; hits: number } | null = null;
        while (j < sheets.length) {
          const span = sheets[j] - sheets[i] + 1;
          const hits = j - i + 1;
          if (hits / span >= minRunDensity && hits >= minRunPages) best = { i, j, hits };
          if (j + 1 < sheets.length) {
            const gapSpan = sheets[j + 1] - sheets[i] + 1;
            if ((j + 2) / gapSpan < minRunDensity - 0.15) break;
          }
          j++;
        }
        if (best) {
          for (let t = best.i; t <= best.j; t++) {
            const sheet = sheets[t];
            if (taken.has(sheet)) continue;
            taken.add(sheet);
            assign.set(sheet, { value: bySheet.get(sheet)!.c.value, kind });
          }
          runs.push({
            kind,
            offset: k,
            firstSheet: sheets[best.i],
            lastSheet: sheets[best.j],
            hits: best.hits,
          });
          i = best.j + 1;
        } else {
          i++;
        }
      }
    }
  }

  runs.sort((a, b) => a.firstSheet - b.firstSheet);
  return { assign, runs };
}

export function formatFolio(entry: { value: number; kind: 'arabic' | 'roman' }): string {
  return entry.kind === 'roman' ? intToRoman(entry.value) : String(entry.value);
}

/** Above this share of self-describing labels, getPageLabels() is discarded. */
export const DEGENERACY_LIMIT = 0.6;

/**
 * Can getPageLabels() be believed for this document?
 *
 * A first pass at this shipped a binary test — "does any non-blank label differ
 * from its sheet index" — and it was measurably wrong twice over. It let
 * through a paper whose labels run 1-11 and then restart 1-5 while the document
 * prints no folio at all, and it was so brittle about blanks that it condemned
 * /PageLabels wholesale on the strength of one bad document.
 *
 * The replacement is a degeneracy SCORE: what share of the non-blank labels
 * simply restate the sheet index? Measured over 24 documents, this separates
 * cleanly with nothing near the boundary — the six untrustworthy documents
 * score 0.688 to 1.000, and every trustworthy one scores 0.000 to 0.005.
 *
 * Why it matters that this is right rather than merely safe: across 3,429 pages
 * of 24 documents, filtered labels and the harvested folio fired together on
 * 2,710 pages and DISAGREED ON NONE OF THEM. On the two scanned books — the
 * most realistic material in the study, and the kind of file a reader is most
 * likely to actually have — the labels beat the harvester by 8 to 20 points,
 * because OCR'd folios are exactly what a harvester struggles to read. Refusing
 * to read them would have thrown away 276 correctly numbered pages to guard
 * against a failure that a single ratio detects.
 *
 * Blank and missing entries are absent, never labels. getPageLabels() returns a
 * full-length array whose front-matter entries are empty strings, so a naive
 * `if (labels) use them` prints an empty printed-page number for the first
 * thirty sheets of a scanned book.
 */
export function labelsUsable(labels: string[] | null | undefined): boolean {
  if (!labels || labels.length === 0) return false;
  let meaningful = 0;
  let selfDescribing = 0;
  for (let i = 0; i < labels.length; i++) {
    const label = (labels[i] ?? '').trim();
    if (!label) continue;
    meaningful++;
    if (label === String(i + 1)) selfDescribing++;
  }
  if (meaningful === 0) return false;
  return selfDescribing / meaningful < DEGENERACY_LIMIT;
}
